// components/HalloweenExtras.js
import React, { useEffect, useState } from 'react';
import './HalloweenExtras.css';

// Extra Halloween decorations layered on top of the halloween_day /
// halloween_night particle presets (rendered by SeasonalBackground whenever
// the active theme uses either one):
//   • spiderwebs in the top corners, with a spider that drops on its thread
//     now and then (both modes)
//   • a flickering full moon + low drifting fog (dark mode only — CSS-gated)
//   • a ghost that occasionally peeks in from a screen edge and waves
// Everything is pointer-events:none, so none of it can block a click.

// Spiderweb drawn for the top-left corner; the top-right one is the same SVG
// mirrored with CSS. Stroke uses currentColor so light/dark set the color.
function Spiderweb({ className }) {
  const spokes = [0, 15, 30, 45, 60, 75, 90];
  const rings = [30, 60, 95, 135, 180];
  const pt = (r, deg) => {
    const a = (deg * Math.PI) / 180;
    return [r * Math.cos(a), r * Math.sin(a)];
  };
  return (
    <svg className={className} width="200" height="200" viewBox="0 0 200 200" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round">
        {spokes.map(d => {
          const [x, y] = pt(200, d);
          return <line key={d} x1="0" y1="0" x2={x} y2={y} />;
        })}
        {/* Rings sag slightly between spokes (quadratic curves pulled toward
            the corner), which reads as a web rather than a target. */}
        {rings.map(r => (
          <path key={r} d={spokes.slice(1).reduce((acc, d, i) => {
            const [x, y] = pt(r, d);
            const [cx, cy] = pt(r * 0.86, (spokes[i] + d) / 2);
            return `${acc} Q ${cx} ${cy} ${x} ${y}`;
          }, `M ${pt(r, spokes[0]).join(' ')}`)} />
        ))}
      </g>
    </svg>
  );
}

// Ghost peek timing: first appearance shortly after load, then every 45-90s.
const FIRST_PEEK_MS = 8000;
const nextPeekDelay = () => 45000 + Math.random() * 45000;
const PEEK_DURATION_MS = 5200; // must match .halloween-ghost animation length

export default function HalloweenExtras() {
  const [peek, setPeek] = useState(null); // { id, side, top } while peeking

  useEffect(() => {
    let timer;
    const schedule = (delay) => {
      timer = setTimeout(() => {
        setPeek({ id: Date.now(), side: Math.random() < 0.5 ? 'left' : 'right', top: 25 + Math.random() * 50 });
        timer = setTimeout(() => {
          setPeek(null);
          schedule(nextPeekDelay());
        }, PEEK_DURATION_MS);
      }, delay);
    };
    schedule(FIRST_PEEK_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div className="halloween-extras" aria-hidden="true">
        <Spiderweb className="halloween-web halloween-web--left" />
        <Spiderweb className="halloween-web halloween-web--right" />

        {/* Spider hangs from the right-hand web; the thread grows with it */}
        <div className="halloween-spider">
          <span className="halloween-spider-thread" />
          <span className="halloween-spider-body">🕷️</span>
        </div>

        <div className="halloween-moon" />
        {/* Mist wisps drifting across the whole night sky (dark mode only) */}
        <div className="halloween-mist halloween-mist--a" />
        <div className="halloween-mist halloween-mist--b" />
        <div className="halloween-mist halloween-mist--c" />
        <div className="halloween-fog halloween-fog--a" />
        <div className="halloween-fog halloween-fog--b" />
      </div>

      {/* Outside .halloween-extras on purpose: that layer is z-index:-1
          (behind content), and the ghost has to show above it. */}
      {peek && (
        <span key={peek.id} aria-hidden="true" className={`halloween-ghost halloween-ghost--${peek.side}`} style={{ top: `${peek.top}%` }}>
          <span className="halloween-ghost-wave">👻</span>
        </span>
      )}
    </>
  );
}
